import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { websiteAnalyticsApi } from '@/api/websiteAnalytics';
import { formatLeadStatusLabel, leadStatusBadgeClass } from '@/lib/crmLeadStyles';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

function Stat({
  label,
  value,
  hint,
  onClick,
}: {
  label: string;
  value: string | number;
  hint?: string;
  onClick?: () => void;
}) {
  const className = [
    'rounded-lg border bg-white p-4 shadow-sm text-left w-full',
    onClick ? 'cursor-pointer transition-colors hover:border-teal-300 hover:shadow-md' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold brand-stat-value tabular-nums">{value}</p>
        {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
        <p className="text-xs text-teal-700 mt-2">Click to view leads</p>
      </button>
    );
  }

  return (
    <div className={className}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold brand-stat-value tabular-nums">{value}</p>
      {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
    </div>
  );
}

function formatMs(ms: number | null | undefined) {
  if (ms == null) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

export default function WebsiteAnalyticsPage() {
  const { user, activeHospitalId, isChainAdmin } = useAuth();
  const [days, setDays] = useState(30);
  const [domain, setDomain] = useState<string>('all');
  const [websiteLeadsOpen, setWebsiteLeadsOpen] = useState(false);
  const viewingHospital =
    user?.hospitals?.find((h) => h.id === (activeHospitalId || user?.hospitalId))?.name ??
    'this hospital unit';

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ['website-analytics', days, domain],
    queryFn: () =>
      websiteAnalyticsApi.getDashboard({
        days,
        domain: domain === 'all' ? null : domain,
      }),
  });

  const {
    data: websiteLeads,
    isLoading: websiteLeadsLoading,
    error: websiteLeadsError,
  } = useQuery({
    queryKey: ['website-analytics-leads', days, activeHospitalId || user?.hospitalId],
    queryFn: () => websiteAnalyticsApi.getWebsiteLeads({ days }),
    enabled: websiteLeadsOpen,
  });

  const chartData = useMemo(
    () =>
      (data?.dailyTrend ?? []).map((d) => ({
        day: d.date.slice(5),
        pageviews: d.pageviews,
        visitors: d.uniqueVisitors,
        sessions: d.sessions,
      })),
    [data?.dailyTrend]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <p className="text-destructive p-6">
        Could not load website analytics. Apply the latest database migration on the server, then
        ensure the TrueArtis site is sending events (THMS analytics env on the website).
      </p>
    );
  }

  const s = data.summary;
  const showSetupHint = s.pageviews === 0 && !data.lastEventAt;

  return (
    <div className="max-w-6xl space-y-6 pb-10">
      {showSetupHint ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-medium">No pageviews recorded yet for {viewingHospital}.</p>
          <p className="mt-1 text-amber-900/90">
            CRM website leads can still appear (forms post server-side). Pageviews need the{' '}
            <code className="text-xs">thms-analytics.js</code> beacon on trueartis.com with{' '}
            <code className="text-xs">THMS_LEAD_API_KEY</code>, <code className="text-xs">THMS_HOSPITAL_ID</code>,
            and lead ingest URL (analytics collect URL is auto-derived from ingest URL on the latest
            Website deploy). On the website server: pull latest code,{' '}
            <code className="text-xs">php artisan config:clear</code>, reload php-fpm, then hard-refresh
            the site and check DevTools → Network for{' '}
            <code className="text-xs">website-analytics/collect</code>.
            {isChainAdmin ? (
              <>
                {' '}
                Ensure <code className="text-xs">THMS_HOSPITAL_ID</code> matches the hospital selected
                in the header ({viewingHospital}).
              </>
            ) : null}
          </p>
        </div>
      ) : null}
      {s.pageviews === 0 && data.lastEventAt ? (
        <p className="text-sm text-muted-foreground">
          No traffic in the last {data.period.days} days. Last pageview recorded:{' '}
          {new Date(data.lastEventAt).toLocaleString()}.
        </p>
      ) : null}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold brand-page-title">Website analytics</h1>
          <p className="text-muted-foreground mt-1">
            First-party traffic for your marketing website (e.g. trueartis.com): visits, unique
            visitors, top pages, referrers, campaigns, and page load performance to guide SEO and
            speed improvements.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Last {data.period.days} days · Updated {new Date(data.generatedAt).toLocaleString()}
            {isFetching && !isLoading ? ' · Refreshing…' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          {data.domains.length > 0 ? (
            <Select value={domain} onValueChange={setDomain}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Domain" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All domains</SelectItem>
                {data.domains.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <button
            type="button"
            className="text-sm underline-offset-4 hover:underline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Pageviews" value={s.pageviews.toLocaleString()} />
        <Stat label="Unique visitors" value={s.uniqueVisitors.toLocaleString()} />
        <Stat label="Sessions" value={s.sessions.toLocaleString()} />
        <Stat
          label="Pages / session"
          value={s.pagesPerSession}
          hint={`Bounce rate ${s.bounceRatePct}%`}
        />
        <Stat label="New visitors" value={s.newVisitors.toLocaleString()} />
        <Stat label="Returning visitors" value={s.returningVisitors.toLocaleString()} />
        <Stat label="Avg page load" value={formatMs(s.avgLoadTimeMs)} hint="Navigation Timing" />
        <Stat label="Avg TTFB" value={formatMs(s.avgTtfbMs)} hint="Time to first byte" />
        <Stat
          label="Website leads (CRM)"
          value={s.websiteLeads.toLocaleString()}
          hint={`${s.leadConversionPct}% of sessions`}
          onClick={s.websiteLeads > 0 ? () => setWebsiteLeadsOpen(true) : undefined}
        />
      </div>

      <Dialog open={websiteLeadsOpen} onOpenChange={setWebsiteLeadsOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Website leads (CRM)</DialogTitle>
            <DialogDescription>
              Leads ingested from your marketing website with source WEBSITE in the last {days} days.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {websiteLeadsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
              </div>
            ) : websiteLeadsError ? (
              <p className="text-sm text-destructive py-6">Could not load website leads.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Specialisation</TableHead>
                    <TableHead>Campaign / details</TableHead>
                    <TableHead className="text-right">Open</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(websiteLeads?.items ?? []).map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {new Date(lead.createdAt).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata',
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{lead.patientName}</div>
                        {lead.email ? (
                          <div className="text-xs text-muted-foreground">{lead.email}</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="tabular-nums">{lead.phone}</TableCell>
                      <TableCell>
                        <span className={leadStatusBadgeClass(lead.status)}>
                          {formatLeadStatusLabel(lead.status)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {lead.assignedTo?.name ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm max-w-[140px] truncate">
                        {lead.specialisation || '—'}
                      </TableCell>
                      <TableCell className="text-xs max-w-[180px]">
                        <div className="truncate">{lead.campaignName || lead.sourceDetails || '—'}</div>
                        {lead.utmSource || lead.utmMedium ? (
                          <div className="text-muted-foreground truncate">
                            {[lead.utmSource, lead.utmMedium].filter(Boolean).join(' / ')}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/hospital-admin/leads?openLeadId=${lead.id}`}
                          className="text-sm text-teal-700 hover:underline"
                          onClick={() => setWebsiteLeadsOpen(false)}
                        >
                          View
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(websiteLeads?.items ?? []).length === 0 && !websiteLeadsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No website leads in this period.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            )}
            {websiteLeads && websiteLeads.total > websiteLeads.items.length ? (
              <p className="text-xs text-muted-foreground mt-3 px-1">
                Showing latest {websiteLeads.items.length} of {websiteLeads.total} leads.
              </p>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Daily traffic</CardTitle>
          <CardDescription>Pageviews, unique visitors, and sessions (IST days)</CardDescription>
        </CardHeader>
        <CardContent className="h-72">
          {chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pageviews recorded yet for this period.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} width={48} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="pageviews"
                  stroke="#0d9488"
                  strokeWidth={2}
                  dot={false}
                  name="Pageviews"
                />
                <Line
                  type="monotone"
                  dataKey="visitors"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={false}
                  name="Unique visitors"
                />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                  name="Sessions"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top pages</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Page</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Unique</TableHead>
                  <TableHead className="text-right">Load</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.topPages.map((row) => (
                  <TableRow key={row.pagePath}>
                    <TableCell className="font-mono text-xs max-w-[220px] truncate">
                      {row.pagePath}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.pageviews}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.uniqueVisitors}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {formatMs(row.avgLoadTimeMs)}
                    </TableCell>
                  </TableRow>
                ))}
                {data.topPages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground">
                      No data yet.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top referrers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {data.topReferrers.map((r) => (
                <div key={r.referrer} className="flex justify-between gap-2">
                  <span className="truncate">{r.referrer}</span>
                  <span className="tabular-nums shrink-0">{r.pageviews}</span>
                </div>
              ))}
              {data.topReferrers.length === 0 ? (
                <p className="text-muted-foreground">No data yet.</p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Campaign / UTM sources</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Medium</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.utmSources.map((r, i) => (
                  <TableRow key={`${r.utmSource}-${r.utmMedium}-${i}`}>
                    <TableCell>{r.utmSource}</TableCell>
                    <TableCell>{r.utmMedium}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.pageviews}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.sessions}</TableCell>
                  </TableRow>
                ))}
                {data.utmSources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground">
                      No tagged campaign traffic yet.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Devices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {data.devices.map((r) => (
                <div key={r.deviceType} className="flex justify-between">
                  <span className="capitalize">{r.deviceType}</span>
                  <span className="tabular-nums">{r.pageviews}</span>
                </div>
              ))}
              {data.devices.length === 0 ? (
                <p className="text-muted-foreground">No data yet.</p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      {data.whatsApp ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">WhatsApp → website journeys</CardTitle>
            <CardDescription>
              Traffic tagged with WhatsApp UTMs (welcome template or broadcast). Session paths and OPD
              self-bookings linked to welcome or WhatsApp UTM on the lead.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="WA sessions" value={data.whatsApp.summary.sessions} />
              <Stat label="WA pageviews" value={data.whatsApp.summary.pageviews} />
              <Stat
                label="Book page views"
                value={data.whatsApp.summary.bookAppointmentPageviews}
                hint="/bookappointments"
              />
              <Stat
                label="OPD booked (welcome WA)"
                value={data.whatsApp.summary.opdBookingsFromWelcome}
              />
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2">Clicks by link (utm_content)</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Link (utm_content)</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Sessions</TableHead>
                    <TableHead className="text-right">Pageviews</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.whatsApp.byLink.map((row) => (
                    <TableRow key={`${row.utmContent}-${row.utmCampaign}`}>
                      <TableCell className="font-mono text-xs">{row.utmContent}</TableCell>
                      <TableCell className="text-xs">{row.utmCampaign}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.sessions}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.pageviews}</TableCell>
                    </TableRow>
                  ))}
                  {data.whatsApp.byLink.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground">
                        No WhatsApp-tagged traffic yet. Add UTMs to welcome template links and re-approve in
                        Meta.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2">Recent session journeys</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Started (IST)</TableHead>
                    <TableHead>Entry link</TableHead>
                    <TableHead>Pages visited</TableHead>
                    <TableHead>Book flow</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.whatsApp.journeys.map((j) => (
                    <TableRow key={j.sessionId}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(j.startedAt).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata',
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </TableCell>
                      <TableCell className="text-xs font-mono">{j.utmContent ?? '—'}</TableCell>
                      <TableCell className="text-xs font-mono max-w-md truncate">
                        {j.pagePaths.join(' → ')}
                      </TableCell>
                      <TableCell>{j.reachedBookAppointments ? 'Yes' : '—'}</TableCell>
                    </TableRow>
                  ))}
                  {data.whatsApp.journeys.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground">
                        No journeys recorded in this period.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2">OPD self-bookings (WhatsApp attribution)</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booked</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>UTM content</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead className="text-right">CRM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.whatsApp.bookings.map((b) => (
                    <TableRow key={b.leadId}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(b.websiteOpdBookedAt).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata',
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{b.patientName}</div>
                        <div className="text-xs text-muted-foreground tabular-nums">{b.phone}</div>
                      </TableCell>
                      <TableCell className="text-xs font-mono">{b.utmContent ?? '—'}</TableCell>
                      <TableCell className="text-xs">{b.websiteOpdBookingChannel ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/hospital-admin/leads?leadId=${b.leadId}`}
                          className="text-teal-700 text-xs underline"
                        >
                          Open lead
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.whatsApp.bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground">
                        No OPD bookings with WhatsApp welcome or UTM in this period.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Slowest pages (performance)</CardTitle>
          <CardDescription>
            Pages with 3+ samples, ranked by slowest server response (TTFB) or full load. Targets:
            TTFB under 200ms (good), under 800ms (acceptable); full load under 2.5s on mobile. Fix
            analytics showed 0ms load when beacons fired too early — deploy updated{' '}
            <code className="text-xs">thms-analytics.js?v=3</code> on the marketing site.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page</TableHead>
                <TableHead className="text-right">Samples</TableHead>
                <TableHead className="text-right">Avg load</TableHead>
                <TableHead className="text-right">Avg TTFB</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.slowPages.map((row) => (
                <TableRow key={row.pagePath}>
                  <TableCell className="font-mono text-xs">{row.pagePath}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.samples}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMs(row.avgLoadTimeMs)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMs(row.avgTtfbMs)}
                  </TableCell>
                </TableRow>
              ))}
              {data.slowPages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">
                    Performance samples will appear after visitors load pages with the analytics beacon.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
